import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { NivelHierarquico } from '../models/atividade';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';

interface ModalGerenciarItensTemplateProps {
  aberto: boolean;
  onFechar: () => void;
}

const NIVEIS: NivelHierarquico[] = ['FASE', 'PROCESSO', 'MACROTAREFA', 'TAREFA', 'SUBTAREFA'];

// Etapas de setup TEMPLATES_SLA (prazoDiasUteis) e EVIDENCIAS_REGRAS (evidenciaObrigatoria/
// aprovacaoSoxObrigatoria) — mesmo endpoint e mesma entidade (MaestroTemplateItem, RN-22) pros
// dois, por isso é uma tela só. Backend já tinha CRUD completo, só faltava a tela.
const ModalGerenciarItensTemplate: React.FC<ModalGerenciarItensTemplateProps> = ({ aberto, onFechar }) => {
  const { areas } = useMaestroContext();
  const [versoes, setVersoes] = useState<any[]>([]);
  const [templateVersionId, setTemplateVersionId] = useState(0);
  const [itens, setItens] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [nivelHierarquico, setNivelHierarquico] = useState<NivelHierarquico>('TAREFA');
  const [areaId, setAreaId] = useState(0);
  const [prazoDiasUteis, setPrazoDiasUteis] = useState(1);
  const [evidenciaObrigatoria, setEvidenciaObrigatoria] = useState(false);
  const [aprovacaoSoxObrigatoria, setAprovacaoSoxObrigatoria] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    maestroService.listarTemplateVersions()
      .then(v => { setVersoes(v); if (v.length > 0) setTemplateVersionId(v[v.length - 1].id); })
      .catch(() => setVersoes([]));
  }, [aberto]);

  useEffect(() => {
    if (!templateVersionId) { setItens([]); setCarregando(false); return; }
    setCarregando(true);
    maestroService.listarTemplateItems(templateVersionId).then(setItens).catch(() => setItens([])).finally(() => setCarregando(false));
  }, [templateVersionId]);

  const criar = () => {
    if (!templateVersionId || !codigo.trim() || !nome.trim()) return;
    setEnviando(true);
    maestroService.criarTemplateItem(templateVersionId, {
      codigo: codigo.trim(), nivelHierarquico, areaId: areaId || undefined, nome: nome.trim(),
      prazoDiasUteis, evidenciaObrigatoria, aprovacaoSoxObrigatoria,
    })
      .then(() => {
        setCodigo(''); setNome(''); setAreaId(0); setPrazoDiasUteis(1); setEvidenciaObrigatoria(false); setAprovacaoSoxObrigatoria(false);
        maestroService.listarTemplateItems(templateVersionId).then(setItens);
      })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar o item.'))
      .finally(() => setEnviando(false));
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='md' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Atividades-Modelo — Prazos (SLA) e Regras de Evidência</DialogTitle>
      <DialogContent>
        {versoes.length === 0
          ? <p className='mc-texto-danger'>Nenhum modelo de fechamento criado ainda — crie um na etapa "Modelo" antes de continuar.</p>
          : (
            <>
              <label className='mc-campo'>
                Modelo de fechamento
                <select value={templateVersionId} onChange={e => setTemplateVersionId(Number(e.target.value))}>
                  {versoes.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
                </select>
              </label>

              {carregando
                ? <p className='mc-texto-neutro'>Carregando...</p>
                : itens.length === 0
                  ? <p className='mc-texto-neutro'>Nenhuma atividade-modelo cadastrada neste modelo ainda.</p>
                  : (
                    <table className='mc-logs-table'>
                      <thead><tr><th>Código</th><th>Nome</th><th>Nível</th><th>Prazo (dias úteis)</th><th>Evidência</th><th>SOX</th></tr></thead>
                      <tbody>
                        {itens.map(it => (
                          <tr key={it.id}>
                            <td>{it.codigo}</td>
                            <td>{it.nome}</td>
                            <td>{it.nivelHierarquico}</td>
                            <td>{it.prazoDiasUteis}</td>
                            <td>{it.evidenciaObrigatoria ? <i className='bi bi-check-lg mc-texto-success' /> : '—'}</td>
                            <td>{it.aprovacaoSoxObrigatoria ? <i className='bi bi-check-lg mc-texto-success' /> : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

              <div className='mc-form-inline' style={{ marginTop: 18 }}>
                <label>Código<input type='text' value={codigo} onChange={e => setCodigo(e.target.value)} placeholder='Ex.: FISC-01' /></label>
                <label>Nome<input type='text' value={nome} onChange={e => setNome(e.target.value)} placeholder='Ex.: Apuração de ICMS' /></label>
                <label>Nível hierárquico
                  <select value={nivelHierarquico} onChange={e => setNivelHierarquico(e.target.value as NivelHierarquico)}>
                    {NIVEIS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label>Área (opcional)
                  <select value={areaId} onChange={e => setAreaId(Number(e.target.value))}>
                    <option value={0}>Sem área específica</option>
                    {areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                  </select>
                </label>
                <label>Prazo (dias úteis)<input type='number' min={0} value={prazoDiasUteis} onChange={e => setPrazoDiasUteis(Number(e.target.value))} /></label>
              </div>
              <label className='mc-checkbox'><input type='checkbox' checked={evidenciaObrigatoria} onChange={e => setEvidenciaObrigatoria(e.target.checked)} /> Evidência obrigatória</label>{' '}
              <label className='mc-checkbox'><input type='checkbox' checked={aprovacaoSoxObrigatoria} onChange={e => setAprovacaoSoxObrigatoria(e.target.checked)} /> Aprovação SOX obrigatória</label>
              <div style={{ marginTop: 14 }}>
                <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !codigo.trim() || !nome.trim()} onClick={criar}>
                  {enviando ? 'Adicionando...' : 'Adicionar atividade-modelo'}
                </button>
              </div>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalGerenciarItensTemplate;
